import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ToggleSwitch from './ToggleSwitch';
import { FaSave, FaCheckCircle } from 'react-icons/fa';

const GeneralSettings = () => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load maintenance mode from localStorage on component mount
  useEffect(() => {
    const storedValue = localStorage.getItem('maintenance_mode');
    if (storedValue !== null) {
      setMaintenanceMode(storedValue === 'true');
    }
  }, []);

  // Save maintenance mode to localStorage
  const handleMaintenanceModeChange = (value) => {
    setMaintenanceMode(value);
    localStorage.setItem('maintenance_mode', value.toString());
    
    // Show saved indicator
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">General Settings</h1>
          <p className="text-gray-400">Manage your website's general configuration</p>
        </div>
        
        {saved && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500 rounded-lg text-green-400"
          >
            <FaCheckCircle />
            <span className="font-medium">Saved!</span>
          </motion.div>
        )}
      </div>

      {/* Maintenance Mode Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0a0a1f] border border-gray-800 rounded-2xl p-6"
      >
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white mb-2">Website Maintenance</h2>
          <p className="text-gray-400 text-sm">
            Control your website's availability to visitors
          </p>
        </div>

        <ToggleSwitch
          enabled={maintenanceMode}
          onChange={handleMaintenanceModeChange}
          label="Maintenance Mode"
          description="When enabled, visitors will see a maintenance page instead of your website"
        />

        {/* Status Info */}
        <div className="mt-6 p-4 bg-[#1a1a3e] border border-gray-800 rounded-xl">
          <div className="flex items-start gap-3">
            <div className={`
              w-3 h-3 rounded-full mt-1
              ${maintenanceMode ? 'bg-orange-500 animate-pulse' : 'bg-green-500'}
            `} />
            <div>
              <p className="text-white font-medium mb-1">
                {maintenanceMode ? 'Website is in Maintenance Mode' : 'Website is Live'}
              </p>
              <p className="text-gray-400 text-sm">
                {maintenanceMode 
                  ? 'Visitors will see a maintenance message. Only admins can access the site.'
                  : 'Your website is accessible to all visitors.'
                }
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Additional Settings Placeholder */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#0a0a1f] border border-gray-800 rounded-2xl p-6"
      >
        <h2 className="text-xl font-bold text-white mb-4">More Settings</h2>
        <div className="text-center py-8">
          <p className="text-gray-500">Additional settings will be added here</p>
        </div>
      </motion.div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-500/10 border border-blue-500/50 rounded-xl p-4"
      >
        <p className="text-blue-400 text-sm">
          <strong>Note:</strong> All settings are saved automatically to your browser's local storage.
          Changes take effect immediately.
        </p>
      </motion.div>
    </div>
  );
};

export default GeneralSettings;
